import { useMemo, useState } from 'react'
import { useActivities, useDeleteActivity } from '../hooks/useActivities'
import { useLibraryActivities, useCopyActivitiesFromLibrary } from '../hooks/useLibraryActivities'
import { useArchived } from '../contexts/ArchiveContext'
import { ActivityEditor } from './ActivityEditor'
import { Icon } from './Icon'
import {
  ModalShell, CancelButton, PrimaryButton, ServerError
} from './modal-form'
import {
  LIBRARY_ACTIVITY_CATEGORIES,
  type Activity,
  type LibraryActivity,
  type LibraryActivityCategory
} from '../types'

// ActivitiesTab - the per-event Activities content. Card grid mirrors the
// prototype's ActivitiesTab: hover-highlight cards, click to open editor,
// "+ Add activity" CTA top right. Description clamped to ~3 lines on the
// card so they stay uniform-ish in height.
//
// Loading / error states render in-place rather than blocking the whole
// EventDetail page - the rest of the page (header, tabs, Overview when
// switched back) keeps working even if this list fails.

interface Props {
  eventId: number
}

export function ActivitiesTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: activities, isLoading, error } = useActivities(eventId)
  const deleteMut = useDeleteActivity(eventId)

  const [editing, setEditing] = useState<Activity | null>(null)
  const [creating, setCreating] = useState(false)
  const [picking, setPicking] = useState(false)

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading activities…</div>
  }

  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load activities</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const list = activities ?? []

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl">Programme &amp; activities</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            What visitors do once they're on site. Click any card to edit.
          </p>
        </div>
        {!archived && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="px-3 py-2 rounded text-sm font-semibold border border-line bg-white hover:border-brass transition-colors whitespace-nowrap"
            >
              + From library
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="px-3 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors whitespace-nowrap"
            >
              + Add custom
            </button>
          </div>
        )}
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="calendar" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No activities yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Activities are what visitors actually do once they're on site —
            Photo Ops, character meet-and-greets, marquee shows, performances.
            Start with the headline one and build out from there.
          </p>
          {!archived && (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setPicking(true)}
                className="px-4 py-2 rounded text-sm font-semibold border border-line bg-white hover:border-brass transition-colors"
              >
                + From library
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
              >
                + Add custom
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((a) => (
            <ActivityCard key={a.id} activity={a} onClick={archived ? undefined : () => setEditing(a)} />
          ))}
        </div>
      )}

      {!archived && creating && (
        <ActivityEditor
          eventId={eventId}
          activity={null}
          onClose={() => setCreating(false)}
        />
      )}

      {!archived && editing && (
        <ActivityEditor
          eventId={eventId}
          activity={editing}
          deleting={deleteMut.isPending}
          onClose={() => setEditing(null)}
          onDelete={() => {
            // window.confirm is acceptable for a single-user-clicking-a-button
            // delete; if confirm-fatigue becomes a thing we'll swap to an
            // inline confirmation chip in the editor footer.
            const ok = window.confirm(
              `Delete "${editing.name}"? This can't be undone.`
            )
            if (!ok) return
            deleteMut.mutate(editing.id, {
              onSuccess: () => setEditing(null)
            })
          }}
        />
      )}

      {!archived && picking && (
        <AddFromLibraryModal
          eventId={eventId}
          existing={list}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

// One activity card. Built as a <button> rather than a <div> with onClick
// for keyboard accessibility (focus + Enter / Space) and screen-reader
// affordance. text-left because <button> defaults to centred text.
function ActivityCard({ activity, onClick }: { activity: Activity; onClick: (() => void) | undefined }) {
  const interactive = onClick != null
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={
        'group relative text-left bg-white border border-line rounded p-4 transition-colors space-y-2 ' +
        (interactive
          ? 'hover:border-brass focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass/30'
          : 'cursor-default')
      }
    >
      {/* Drag handle - placeholder only; reorder is not wired in v1 but the
          affordance is in the prototype so we surface it on hover. */}
      <span
        aria-hidden
        className="absolute top-2 right-2 text-line opacity-0 group-hover:opacity-100 transition-opacity"
        title="Reorder coming in a later phase"
      >
        <Icon name="grip" size={14} />
      </span>
      <div className="flex items-start justify-between gap-2 pr-5">
        <h3 className="font-semibold text-sm flex-1 leading-tight">{activity.name}</h3>
        {activity.staffCount != null && (
          <span className="text-xs text-ink-soft font-mono whitespace-nowrap flex-shrink-0">
            {activity.staffCount} staff
          </span>
        )}
      </div>

      {activity.librarySlug && (
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: '#dcebe5', color: '#0a4a3a' }}
            title={`From library — ${activity.librarySlug}`}
          >
            from library
          </span>
          {activity.category && (
            <span className="text-[10px] text-ink-soft font-mono">{activity.category}</span>
          )}
        </div>
      )}
      {!activity.librarySlug && activity.category && (
        <div className="text-[10px] text-ink-soft font-mono">{activity.category}</div>
      )}

      {activity.description && (
        <p
          className="text-xs text-ink-soft leading-snug"
          // Inline 3-line clamp - works without the Tailwind line-clamp plugin
          // and keeps the layout tractable when descriptions vary in length.
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {activity.description}
        </p>
      )}

      {(activity.location || activity.timeText || activity.supplier) && (
        <div className="text-xs text-ink-soft space-y-0.5 pt-1">
          {activity.location && (
            <div>
              <span aria-hidden>📍</span> {activity.location}
            </div>
          )}
          {activity.timeText && (
            <div className="font-mono">
              <span aria-hidden>🕐</span> {activity.timeText}
            </div>
          )}
          {activity.supplier && <div className="italic">{activity.supplier}</div>}
        </div>
      )}

      {activity.attachments.length > 0 && (
        <div className="pt-1 mt-1 border-t border-line text-xs text-green font-semibold">
          {activity.attachments.length}{' '}
          {activity.attachments.length === 1 ? 'attachment' : 'attachments'}
        </div>
      )}
    </button>
  )
}

// AddFromLibraryModal — multi-select picker over dbo.LibraryActivities.
// Rows already present on the event (via library_slug match) are
// shown disabled with an "already added" hint so the operator sees
// what's available without confusion. Submit fires
// /events/{id}/activities/copy-from-library with the chosen slugs;
// the server skips any that race-condition in between.

function AddFromLibraryModal({
  eventId, existing, onClose
}: {
  eventId: number
  existing: Activity[]
  onClose: () => void
}) {
  const { data: library, isLoading, error } = useLibraryActivities()
  const copyMut = useCopyActivitiesFromLibrary(eventId)

  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Slugs already on this event — picker hides "add" for these.
  const alreadyAdded = useMemo(() => {
    const s = new Set<string>()
    for (const a of existing) {
      if (a.librarySlug) s.add(a.librarySlug)
    }
    return s
  }, [existing])

  // Group rows by category, preserving canonical order.
  const grouped = useMemo(() => {
    const buckets = new Map<LibraryActivityCategory, LibraryActivity[]>()
    for (const r of library ?? []) {
      const list = buckets.get(r.category) ?? []
      list.push(r)
      buckets.set(r.category, list)
    }
    return LIBRARY_ACTIVITY_CATEGORIES
      .map((cat) => ({ category: cat, rows: buckets.get(cat) ?? [] }))
      .filter((g) => g.rows.length > 0)
  }, [library])

  function toggle(slug: string) {
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  function handleSubmit() {
    if (selected.size === 0 || copyMut.isPending) return
    copyMut.mutate(Array.from(selected), {
      onSuccess: () => onClose()
    })
  }

  const saving = copyMut.isPending
  const serverError = copyMut.error as Error | null
  const selectableCount = (library ?? []).filter((r) => !alreadyAdded.has(r.slug)).length

  return (
    <ModalShell
      title="Add from library"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton
            type="button"
            disabled={selected.size === 0 || saving}
            onClick={handleSubmit}
          >
            {saving
              ? 'Adding…'
              : selected.size === 0
                ? 'Add selected'
                : `Add ${selected.size} activit${selected.size === 1 ? 'y' : 'ies'}`}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <p className="text-sm text-ink-soft">
            Pick activities from the library to copy onto this event. Library
            defaults (location, time, staff, supplier) seed the per-event
            values — you can edit them after adding.
          </p>

          {isLoading && (
            <div className="text-ink-soft text-sm font-mono">Loading library…</div>
          )}

          {error && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
              Could not load library: {(error as Error).message}
            </div>
          )}

          {library && library.length === 0 && (
            <div className="bg-parchment/60 border border-line rounded p-4 text-sm text-ink-soft">
              The Activities library is empty. Ask an admin to add rows at
              <span className="font-mono"> /resources/activities</span>.
            </div>
          )}

          {library && library.length > 0 && selectableCount === 0 && (
            <div className="bg-parchment/60 border border-line rounded p-4 text-sm text-ink-soft">
              Every library activity has already been added to this event.
            </div>
          )}

          {grouped.map(({ category, rows }) => (
            <section key={category}>
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-1.5">
                {category}
              </div>
              <div className="border border-line rounded divide-y divide-line">
                {rows.map((r) => {
                  const taken = alreadyAdded.has(r.slug)
                  const checked = selected.has(r.slug)
                  return (
                    <label
                      key={r.id}
                      className={
                        'flex items-start gap-3 px-3 py-2.5 ' +
                        (taken
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-parchment/40')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={taken || saving}
                        onChange={() => toggle(r.slug)}
                        className="w-4 h-4 mt-0.5 accent-green cursor-pointer disabled:cursor-not-allowed flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{r.name}</span>
                          <span className="text-[10px] text-ink-soft font-mono">{r.slug}</span>
                          {taken && (
                            <span className="text-[10px] uppercase tracking-wider text-ink-soft">
                              already added
                            </span>
                          )}
                        </div>
                        {r.description && (
                          <div className="text-xs text-ink-soft mt-0.5">{r.description}</div>
                        )}
                        {(r.defaultLocation || r.defaultTime || r.defaultStaff != null || r.defaultSupplier) && (
                          <div className="text-[11px] text-ink-soft mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                            {r.defaultLocation && <span>📍 {r.defaultLocation}</span>}
                            {r.defaultTime && <span className="font-mono">🕐 {r.defaultTime}</span>}
                            {r.defaultStaff != null && <span>{r.defaultStaff} staff</span>}
                            {r.defaultSupplier && <span className="italic">{r.defaultSupplier}</span>}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            </section>
          ))}

          {serverError && <ServerError title="Could not add from library" error={serverError} />}
        </div>
      }
    />
  )
}
